import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent
} from '@mui/material';
import useTranslate from 'Hooks/useTranslate';
import { TextInput } from 'Components/Shared';
import { TUsersCreation } from './FormUsersCreation.container';
import FormUsersCreationLayout from './FormUsersCreationLayout';
import { LocalContext } from 'Context/Local.context';
import { useContext, useMemo, useState } from 'react';
import SearchByAccount from 'Components/Shared/SearchBars/SearchByAccount/SearchByAccount';
import { DEBOUNCE_DELAY } from 'Theme/config';
import shortAccountlabel from 'Tools/LabelFormatter/shortAccountLabel';
import { debounce } from 'Tools/debounce';
import { useCurrentUserInfo } from 'Hooks/useCurrentUserInfo';

type Props = {
  onCancel(): void;
  onSubmit(values: TUsersCreation): void;
  loadingData?: boolean;
  loading?: boolean;
  selectorList: IAccount[];
  onSearch: (value: string) => void;
  languages: ILanguage[];
  selectedAccount: IAccount | null;
  selectedAccountCode: string;
  onChooseAccount: (acc: IAccount | null) => void;
};

const FormUsersCreation = ({
  onCancel,
  onSubmit,
  loading,
  loadingData,
  selectorList,
  languages,
  onSearch,
  selectedAccount,
  selectedAccountCode,
  onChooseAccount
}: Props) => {
  const ts = useTranslate();
  const { currentAccount, availableAccounts } = useContext(LocalContext);
  const {
    isAdminOfOnlyOneAccount,
    hasTooManyAccounts,
    hasLimitedAccounts,
    currentAccountExists
  } = useCurrentUserInfo();

  const [filterValue, setFilterValue] = useState<null | string>(null);

  const handleClear = () => {
    onSearch('');
    onChooseAccount(null);
  };

  const handleChange = (event: SelectChangeEvent<string>) => {
    let selectedAcc = availableAccounts.find(
      acc => acc.code === event.target.value
    );
    onChooseAccount(selectedAcc ?? null);
  };

  const debouncedSendRequest = useMemo(() => {
    return debounce(onSearch, DEBOUNCE_DELAY);
  }, [onSearch]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = e.target.value;
    setFilterValue(currentValue.trim());
    debouncedSendRequest(currentValue);
  };

  return (
    <FormUsersCreationLayout
      onCancel={onCancel}
      onSubmit={onSubmit}
      loading={loading}
      languages={languages}
      heading="component.modals.createUser.wizard.heading"
      subheading="component.modals.createUser.wizard.subheading"
      initialAccount={availableAccounts[0]?.code ?? ''}
      submitDisabled={selectedAccount === null}
    >
      {isAdminOfOnlyOneAccount && (
        <TextInput
          name="account"
          variant="outlined"
          size="medium"
          label={ts('component.modals.createUser.labels.accountCode')}
          autoFocus
          value={shortAccountlabel(availableAccounts[0])}
          disabled={true}
        />
      )}
      {!isAdminOfOnlyOneAccount && currentAccount !== null && (
        <TextInput
          name="account"
          variant="outlined"
          size="medium"
          label={ts('component.modals.createUser.labels.accountCode')}
          autoFocus
          value={shortAccountlabel(currentAccount)}
          disabled={true}
        />
      )}
      {!isAdminOfOnlyOneAccount &&
        !currentAccountExists &&
        hasLimitedAccounts && (
          <FormControl fullWidth variant="outlined">
            <InputLabel htmlFor="account">
              {ts('common.terminology.account')}
            </InputLabel>
            <Select
              id="account"
              defaultValue={''}
              label={ts('component.userAccountUpdate.chooseAccount')}
              onChange={handleChange}
              variant="outlined"
              inputProps={{ id: 'account' }}
            >
              {availableAccounts.map((acc: IAccount) => {
                const tiersName = shortAccountlabel(acc);
                return (
                  <MenuItem
                    key={acc.code}
                    value={acc.code}
                    role="menuitem"
                    selected={selectedAccountCode === acc.code}
                  >
                    {tiersName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
      {!currentAccountExists && hasTooManyAccounts && (
        <SearchByAccount
          name="account"
          accounts={selectorList}
          selectedAccount={selectedAccount}
          filterValue={filterValue ?? ''}
          handleSearch={handleSearch}
          handleClear={handleClear}
          handleChooseAccount={(acc: IAccount) => onChooseAccount(acc)}
          loading={loadingData}
          inputLabel={ts('component.modals.createUser.labels.accountCode')}
        />
      )}
    </FormUsersCreationLayout>
  );
};

export default FormUsersCreation;
